<template>
  <er-card v-loading="loading" class="pond-hourly-graph">
    <layout-toolbar slot="header" type="flex" justify="space-between">
      <p class="card-title">{{ $t("Comn_hourly_feed") }}</p>
      <el-col :span="16">
        <layout-toolbar type="flex">
          <el-col :span="10">
            <er-select
              :value="dataViewOptions"
              size="mini"
              @change="handleCommand"
            >
              <el-option
                :label="$t('Pond_raw_data')"
                value="Raw Data"
              ></el-option>
              <el-option
                :label="$t('Comn_avrg_data')"
                value="Average Data"
              ></el-option>
            </er-select>
          </el-col>
          <el-col :span="14">
            <er-date-picker
              v-model="dateRange"
              :disableDateMethod="disableDateMethod"
              @change="handleValueChange"
            ></er-date-picker>
          </el-col>
        </layout-toolbar>
      </el-col>
    </layout-toolbar>
    <el-row :key="$i18n.locale">
      <high-charts
        :options="chartOptions"
        constructor-type="stockChart"
      ></high-charts>
    </el-row>
  </er-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import errorHandlerMixin from "@/mixins/errorHandlerMixin";
import chartMixin from "@/mixins/chartMixin";
import { castISOStringToUserTZ, timeStrHHmmVal } from "@/utils/commonUtils";
import { pondHourlyGraphConfig } from "./chartOptions";

export default {
  mixins: [errorHandlerMixin, chartMixin],
  data: function() {
    return {
      feed_hourly_logs: [],
      dataViewOptions: "Raw Data",
      dateRange: [],
      params: {
        from_date: "",
        to_date: "",
        month_of: null,
        week_of: null
      },
      chartOptions: { ...pondHourlyGraphConfig },
      loading: false
    };
  },
  computed: {
    ...mapGetters("pondDetails", {
      getPondHourlyFeedGraph: "getPondHourlyFeedGraph"
    }),
    ...mapGetters("user", {
      getUserTimeZoneOffset: "getUserTimeZoneOffset",
      getMomentObjectUserTZ: "getMomentObjectUserTZ"
    }),
    dataViewOptionsStr() {
      return {
        "Raw Data": "Pond_raw_data",
        "Average Data": "Comn_avrg_data"
      };
    },
    defaultMomentDtRangeForCurrCulture() {
      return [
        this.getMomentObjectUserTZ()
          .subtract(2, "days")
          .startOf("days"),
        this.getMomentObjectUserTZ()
      ];
    }
  },
  methods: {
    ...mapActions("pondDetails", {
      fetchHourWisePondFeed: "fetchHourWisePondFeed"
    }),
    async initComponent(pondId) {
      this.loading = true;
      try {
        await this.fetchHourWisePondFeed({
          deviceType: "pond",
          params: this.params
        });
        this.feed_hourly_logs = this.getPondHourlyFeedGraph || [];
        this.handleCommand(this.dataViewOptions);
      } catch (err) {
        this.errorMessages(err, true);
      } finally {
        this.loading = false;
        this.initAxisTextKeys("Comn_date", "Comn_feed_kg");
        this.initChartLang();
      }
    },
    pondHourlyFeed(pondFeedDaysWise = [], dateRange) {
      const dayFeedSchedule = {};
      const dayFeedAutomatic = {};
      // const dayStart = this.$moment(dateRange[0] + "T00:00:00.000Z");
      // const dayEnd = this.$moment(dateRange[1] + "T00:00:00.000Z");
      // while (dayStart <= dayEnd) {
      //   dayFeedSchedule[dayStart.valueOf()] = 0;
      //   dayFeedAutomatic[dayStart.valueOf()] = 0;
      //   dayStart.add(1, "hours");
      // }
      pondFeedDaysWise.forEach(dayFeed => {
        // const date = this.$moment.utc(dayFeed.date).unix();
        dayFeed.time_slots.forEach(timeSlotFeed => {
          const isoString = castISOStringToUserTZ(
            dayFeed.date,
            this.getUserTimeZoneOffset
          );
          const correctDate = this.$moment(isoString).startOf("day");
          const dateTimeFeed = correctDate
            .add(timeSlotFeed.s_time_in_seconds, "seconds")
            .valueOf();
          timeSlotFeed.pond_mothers.reduce(
            (acc, pm) => {
              if (acc.dayFeedAutomatic[dateTimeFeed] === undefined) {
                acc.dayFeedAutomatic[dateTimeFeed] = 0;
              }
              if (acc.dayFeedSchedule[dateTimeFeed] === undefined) {
                acc.dayFeedSchedule[dateTimeFeed] = 0;
              }
              if (pm.mode === "AUTOMATIC") {
                acc.dayFeedAutomatic[dateTimeFeed] += pm.dispensed_feed;
              } else {
                acc.dayFeedSchedule[dateTimeFeed] += pm.dispensed_feed;
              }
              return acc;
            },
            { dayFeedSchedule, dayFeedAutomatic }
          );
        });
      });
      let arrDayFeedSchedule = Object.keys(dayFeedSchedule).map(key => {
        return { x: Number(key), y: dayFeedSchedule[key] };
      });
      let arrDayFeedAutomatic = Object.keys(dayFeedAutomatic).map(key => {
        return { x: Number(key), y: dayFeedAutomatic[key] };
      });
      arrDayFeedSchedule = arrDayFeedSchedule.sort((a, b) => a.x - b.x);
      arrDayFeedAutomatic = arrDayFeedAutomatic.sort((a, b) => a.x - b.x);
      return {
        feedSchedule: arrDayFeedSchedule,
        feedAutomatic: arrDayFeedAutomatic
      };
    },
    pondHourlyAvgData: function(pondFeedDaysWise = [], dateRange) {
      const groupByFeedSchedule = {};
      const groupByFeedAutomatic = {};
      const dateRangeDiff =
        this.$moment(dateRange[1] + "T00:00:00.000Z").diff(
          this.$moment(dateRange[0] + "T00:00:00.000Z"),
          "days"
        ) + 1 || 1;
      const totalDayKeys = [];
      const isoString = castISOStringToUserTZ(
        dateRange[0] + "T00:00:00.000Z",
        this.getUserTimeZoneOffset
      );
      for (let hour = 0; hour < 24; hour++) {
        const correctDate = this.$moment(isoString).startOf("day");
        const dateTimeFeed = correctDate.add(hour, "hours").valueOf();
        totalDayKeys.push(dateTimeFeed);
      }
      totalDayKeys.forEach(hour => {
        groupByFeedSchedule[hour] = 0;
        groupByFeedAutomatic[hour] = 0;
      });
      pondFeedDaysWise.forEach(dayFeed => {
        const date = this.$moment(isoString)
          .startOf("day")
          .unix();
        dayFeed.time_slots.forEach(timeSlotFeed => {
          const dateTimeFeed = date + timeSlotFeed.s_time_in_seconds;
          if (
            typeof groupByFeedAutomatic[dateTimeFeed * 1000] === "undefined"
          ) {
            groupByFeedAutomatic[dateTimeFeed * 1000] = 0;
          }
          if (typeof groupByFeedSchedule[dateTimeFeed * 1000] === "undefined") {
            groupByFeedSchedule[dateTimeFeed * 1000] = 0;
          }
          timeSlotFeed.pond_mothers.forEach(pm => {
            if (pm.mode === "AUTOMATIC") {
              groupByFeedAutomatic[dateTimeFeed * 1000] += pm.dispensed_feed;
            } else {
              groupByFeedSchedule[dateTimeFeed * 1000] += pm.dispensed_feed;
            }
          });
        });
      });
      Object.keys(groupByFeedSchedule).forEach(key => {
        groupByFeedSchedule[key] = groupByFeedSchedule[key] / dateRangeDiff;
      });
      Object.keys(groupByFeedAutomatic).forEach(key => {
        groupByFeedAutomatic[key] = groupByFeedAutomatic[key] / dateRangeDiff;
      });
      let resultGroupByFeedSchedule = Object.keys(groupByFeedSchedule).map(
        key => {
          return { x: Number(key), y: groupByFeedSchedule[key] };
        }
      );
      let resultGroupByFeedAutomatic = Object.keys(groupByFeedAutomatic).map(
        key => {
          return { x: Number(key), y: groupByFeedAutomatic[key] };
        }
      );
      resultGroupByFeedSchedule = resultGroupByFeedSchedule.sort(
        (a, b) => a.x - b.x
      );
      resultGroupByFeedAutomatic = resultGroupByFeedAutomatic.sort(
        (a, b) => a.x - b.x
      );
      return {
        groupByFeedSchedule: resultGroupByFeedSchedule,
        groupByFeedAutomatic: resultGroupByFeedAutomatic
      };
    },
    handleValueChange: async function(currentSelectedValues) {
      let from_date = this.$moment(currentSelectedValues[0]);
      let to_date = this.$moment(currentSelectedValues[1]);
      if (!this.checkDateWithInCultureRange(from_date)) {
        from_date = this.getMomentObjArrOfCultureDateRange[0];
      }
      if (!this.checkDateWithInCultureRange(to_date)) {
        to_date = this.getMomentObjArrOfCultureDateRange[1];
      }
      this.params.from_date = from_date.format("YYYY-MM-DD");
      this.params.to_date = to_date.format("YYYY-MM-DD");
      await this.initComponent(this.pondId);
    },
    generateData() {
      const schedulePMsFeed = [];
      const automaticPMsFeed = [];
      const startDate = this.$moment(this.dateRange[0]);
      const timeGap = 1000;
      for (let i = 0; i < timeGap; i++) {
        schedulePMsFeed.push({
          x: startDate.add(i, "minutes").toDate(),
          y: Math.random() * 100
        });
        automaticPMsFeed.push({
          x: startDate.add(i, "minutes").toDate(),
          y: Math.random() * 100
        });
      }
      return { automaticPMsFeed, schedulePMsFeed };
    },
    initChart() {
      let result = {};
      let schedulePMsFeed = [];
      let automaticPMsFeed = [];
      let totalSchedulePMsFeed = 0;
      let totalAutomaticPMsFeed = 0;
      if (this.feed_hourly_logs.length === 0) {
        // let x = this.generateData();
        // let x = { schedulePMsFeed: [], automaticPMsFeed: [] };
        // schedulePMsFeed = x.schedulePMsFeed;
        // automaticPMsFeed = x.automaticPMsFeed;
        this.chartOptions.series = [];
        return;
      } else {
        if (this.dataViewOptions === "Average Data") {
          result = this.pondHourlyAvgData(
            this.feed_hourly_logs || [],
            this.dateRange
          );
          schedulePMsFeed = result.groupByFeedSchedule;
          automaticPMsFeed = result.groupByFeedAutomatic;
        } else if (this.dataViewOptions === "Raw Data") {
          result = this.pondHourlyFeed(
            this.feed_hourly_logs || [],
            this.dateRange
          );
          schedulePMsFeed = result.feedSchedule;
          automaticPMsFeed = result.feedAutomatic;
        }
        totalSchedulePMsFeed = (
          schedulePMsFeed.map(feed => feed.y) || []
        ).reduce((acc, curr) => acc + curr, 0);
        totalAutomaticPMsFeed = (
          automaticPMsFeed.map(feed => feed.y) || []
        ).reduce((acc, curr) => acc + curr, 0);
      }
      this.initChartSeries(
        automaticPMsFeed,
        schedulePMsFeed,
        totalSchedulePMsFeed,
        totalAutomaticPMsFeed
      );
    },
    initChartSeries(
      automaticPMsFeed,
      schedulePMsFeed,
      totalSchedulePMsFeed,
      totalAutomaticPMsFeed
    ) {
      this.chartOptions.series = [];

      this.chartOptions.series.push({
        name: this.$t("PM_automatic"),
        data: automaticPMsFeed,
        color: "#758e32",
        type: "column",
        pointWidth: 10,
        visible: true,
        stacking: "overlap",
        stack: 0
      });
      this.chartOptions.series.push({
        name: this.$t("Comn_schedule"),
        data: schedulePMsFeed,
        color: "#f1b727",
        type: "column",
        pointWidth: 10,
        visible: true,
        stacking: "overlap",
        stack: 0
      });
      this.chartOptions.series.push({
        name: this.$t("Comn_feed_kg"),
        type: "pie",
        data: [
          {
            name: this.$t("Comn_schedule"),
            y: Number(totalSchedulePMsFeed.toFixed(2)),
            color: "#f1b727"
          },
          {
            name: this.$t("PM_automatic"),
            y: Number(totalAutomaticPMsFeed.toFixed(2)),
            color: "#758e32"
          }
        ],
        center: ["90%", "5%"],
        size: 70,
        showInLegend: false,
        dataLabels: {
          enabled: false
        }
      });
    },
    handleCommand(command) {
      this.dataViewOptions = command;
      this.initChart();
    }
  }
};
</script>

<style lang="scss">
@import "../../styles/mixins.scss";
.pond-hourly-graph {
  @include responsiveProperty(
    font-size,
    $app_font_size_1,
    $app_font_size_2,
    $app_font_size_3
  );
}
</style>
